/**
 * "Choose your CV style" segment catalog and suggestion logic. Pure data/logic
 * shared by the native and web renderers
 * (`CvStyleSection.tsx` / `CvStyleSection.web.tsx`).
 *
 * Recommendations are deliberately career-stage based. Residence, UAE PASS
 * verification, language and names are not nationality evidence, so they must
 * never auto-label a person as a national or expat. Legacy Gulf segment ids
 * remain readable for already-saved onboarding preferences, but are hidden
 * from the picker.
 */

export type CvStyleGroup = "career" | "uae" | "gcc" | "expat";
export type CvStyleLevel = "fresh" | "mid" | "managerial" | "exec";

export interface CvStyleSegment {
  /** Stable id used for selection state and i18n keys. */
  id: string;
  /** Seeded skill id used to adapt the CV draft. */
  skillId: string;
  group: CvStyleGroup;
  /** `null` for single-card groups (GCC). */
  level: CvStyleLevel | null;
}

/**
 * Neutral presentation used for every new CV. Onboarding must never run a
 * Skill or guess a career stage just to choose a starting style; users can
 * change the presentation later from the CV Theme controls.
 */
export const DEFAULT_CV_STYLE_SEGMENT_ID = "career-mid";

/**
 * Segment to seeded skillId mapping. Career-stage segments are the current UI;
 * the old Gulf ids are retained only so an existing saved preference can still
 * render and enrich safely.
 */
export const CV_STYLE_SEGMENTS: CvStyleSegment[] = [
  {
    id: "career-fresh",
    skillId: "cv-student",
    group: "career",
    level: "fresh",
  },
  { id: "career-mid", skillId: "cv-rewrite", group: "career", level: "mid" },
  {
    id: "career-managerial",
    skillId: "cv-rewrite",
    group: "career",
    level: "managerial",
  },
  { id: "career-exec", skillId: "cv-rewrite", group: "career", level: "exec" },
  // Backward compatibility for preferences saved before neutral career stages.
  { id: "uae-fresh", skillId: "cv-uae-fresh", group: "uae", level: "fresh" },
  { id: "uae-mid", skillId: "cv-uae-mid", group: "uae", level: "mid" },
  {
    id: "uae-managerial",
    skillId: "cv-uae-managerial",
    group: "uae",
    level: "managerial",
  },
  { id: "uae-exec", skillId: "cv-uae-exec", group: "uae", level: "exec" },
  { id: "gcc-national", skillId: "cv-gcc-national", group: "gcc", level: null },
  {
    id: "expat-fresh",
    skillId: "cv-expat-fresh",
    group: "expat",
    level: "fresh",
  },
  { id: "expat-mid", skillId: "cv-expat-mid", group: "expat", level: "mid" },
  {
    id: "expat-managerial",
    skillId: "cv-expat-managerial",
    group: "expat",
    level: "managerial",
  },
  { id: "expat-exec", skillId: "cv-expat-exec", group: "expat", level: "exec" },
];

export const CV_STYLE_GROUP_ORDER: CvStyleGroup[] = ["career"];

export function getSegmentById(id: string): CvStyleSegment | undefined {
  return CV_STYLE_SEGMENTS.find((s) => s.id === id);
}

export function getSegmentsForGroup(group: CvStyleGroup): CvStyleSegment[] {
  return CV_STYLE_SEGMENTS.filter((s) => s.group === group);
}

function normalize(value?: string | null): string {
  return (value ?? "").trim().toLowerCase();
}

// Seniority inference.

const EXEC_KEYWORDS = [
  "ceo",
  "cfo",
  "coo",
  "cto",
  "cmo",
  "chief",
  "founder",
  "co-founder",
  "president",
  "vice president",
  "vp ",
  " vp",
  "managing director",
  "partner",
  "head of",
  "board ",
  "c-level",
];

const MANAGERIAL_KEYWORDS = [
  "manager",
  "director",
  "lead",
  "head",
  "supervisor",
  "principal",
];

const EARLY_CAREER_KEYWORDS = [
  "intern",
  "internship",
  "trainee",
  "apprentice",
  "student",
  "graduate",
  "volunteer",
];

export interface SeniorityInput {
  /** Number of work-experience entries on the CV draft. */
  experienceCount: number;
  /** Role/title strings + headline used for keyword inference. */
  titles: string[];
  /** Work-entry titles only, so an imported headline cannot skew fresh-grad detection. */
  experienceTitles?: string[];
}

export function inferCvStyleLevel(input: SeniorityInput): CvStyleLevel {
  // Headline + first role represent the current positioning. An old role deep
  // in work history must not turn an individual contributor into a manager.
  const currentBlob = input.titles
    .slice(0, 2)
    .map((t) => ` ${normalize(t)} `)
    .join(" ");
  if (EXEC_KEYWORDS.some((k) => currentBlob.includes(k))) return "exec";
  if (MANAGERIAL_KEYWORDS.some((k) => currentBlob.includes(k))) {
    return "managerial";
  }
  if (input.experienceCount <= 0) return "fresh";

  const roleTitles = (input.experienceTitles ?? input.titles)
    .map(normalize)
    .filter(Boolean);
  if (
    roleTitles.length > 0 &&
    roleTitles.every((title) =>
      EARLY_CAREER_KEYWORDS.some((keyword) => title.includes(keyword)),
    )
  ) {
    return "fresh";
  }

  return "mid";
}

// "Pick for me" pre-selection.

export interface SuggestionInput {
  experienceCount: number;
  titles: string[];
  experienceTitles?: string[];
}

/**
 * Suggested segment id for pre-selection and the "Pick for me" shortcut.
 * The recommendation is based only on career evidence in the CV. Demographic
 * status is never inferred from residence or authentication method.
 */
export function suggestCvStyleSegmentId(input: SuggestionInput): string {
  const level = inferCvStyleLevel({
    experienceCount: input.experienceCount,
    titles: input.titles,
    experienceTitles: input.experienceTitles,
  });
  const id = `career-${level}`;
  return getSegmentById(id)?.id ?? "career-mid";
}
